/** Shared helpers + session storage contract for the OAuth playground. */

export const PG_SESSION_KEY = 'pg_session';
export const PG_CALLBACK_KEY = 'pg_callback';

/** Everything the app would keep between "redirect to authorize" and "callback". */
export interface PlaygroundSession {
  /** authrz.me row id of the client (for linking back). */
  clientRowId: string;
  clientId: string;
  clientName: string;
  redirectUri: string;
  scopes: string[];
  pkce: boolean;
  state: string;
  nonce: string;
  verifier: string;
  resource: string;
}

/** What the callback page received from the authorization server. */
export interface PlaygroundCallback {
  code: string | null;
  state: string | null;
  error: string | null;
  errorDescription: string | null;
  receivedAt: string;
  url: string;
}

export function b64url(bytes: Uint8Array): string {
  let bin = '';
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

export function randomToken(bytes: number): string {
  return b64url(crypto.getRandomValues(new Uint8Array(bytes)));
}

export async function s256(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
  return b64url(new Uint8Array(digest));
}

export function decodeJwtPart(part: string | undefined): Record<string, unknown> | null {
  if (!part) return null;
  try {
    const padded = part.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(padded.padEnd(padded.length + ((4 - (padded.length % 4)) % 4), '='))
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join('')
    );
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function decodeJwt(token: string | undefined | null): {
  header: Record<string, unknown> | null;
  payload: Record<string, unknown> | null;
  parts: [string, string, string] | null;
} {
  if (!token) return { header: null, payload: null, parts: null };
  const parts = token.split('.');
  if (parts.length !== 3) return { header: null, payload: null, parts: null };
  return {
    header: decodeJwtPart(parts[0]),
    payload: decodeJwtPart(parts[1]),
    parts: parts as [string, string, string],
  };
}

export function readSession<T>(key: string): T | null {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

export function writeSession(key: string, value: unknown): void {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage unavailable */
  }
}

export function clearSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* storage unavailable */
  }
}
